import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 0;
const BRIDGE_TOLERANCE = 50;
const CLOSE_ANIMATION_DURATION = 400;

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #isInsideBridge = false;
  #isClosing = false;
  #closeTimeout = null;
  #menuLinkRect = null;
  #submenuRect = null;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    document.addEventListener('mousemove', this.#handleMouseMove, {
      signal: this.#abortController.signal,
      passive: true,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #state = {
    activeItem: null,
  };

  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  #handleMouseMove = (event) => {
    if (!this.#state.activeItem || this.#isClosing) return;

    const submenu = findSubmenu(this.#state.activeItem);
    if (!submenu) return;

    const submenuRect = submenu.getBoundingClientRect();
    const menuLinkRect = this.#state.activeItem.getBoundingClientRect();
    
    const isInSubmenu = this.#isPointInRect(event.clientX, event.clientY, submenuRect, BRIDGE_TOLERANCE);
    const isInMenuLink = this.#isPointInRect(event.clientX, event.clientY, menuLinkRect, 5);
    const isInBridge = this.#isPointInBridge(event.clientX, event.clientY, menuLinkRect, submenuRect);

    if (isInSubmenu || isInMenuLink || isInBridge) {
      this.#isInsideBridge = true;
      this.#debouncedDeactivate.cancel();
    } else {
      if (this.#isInsideBridge) {
        this.#isInsideBridge = false;
        this.#deactivate();
      }
    }
  };

  #isPointInRect(x, y, rect, tolerance = 0) {
    return (
      x >= rect.left - tolerance &&
      x <= rect.right + tolerance &&
      y >= rect.top - tolerance &&
      y <= rect.bottom + tolerance
    );
  }

  #isPointInBridge(x, y, linkRect, submenuRect) {
    const linkCenterX = linkRect.left + linkRect.width / 2;
    const linkBottom = linkRect.bottom;
    const submenuTop = submenuRect.top;
    const submenuLeft = submenuRect.left;
    const submenuRight = submenuRect.right;

    if (y < linkBottom || y > submenuTop + 20) return false;

    const progress = (y - linkBottom) / (submenuTop - linkBottom);
    const leftBound = linkCenterX + (submenuLeft - linkCenterX) * progress - BRIDGE_TOLERANCE;
    const rightBound = linkCenterX + (submenuRight - linkCenterX) * progress + BRIDGE_TOLERANCE;

    return x >= leftBound && x <= rightBound;
  }

  activate = (event) => {
    // A menu is mid-close (400ms animation). Re-hovering must interrupt that close and
    // reopen — otherwise the pointer is already sitting on the item, no new pointerenter
    // will fire, and the menu stays stuck shut until the cursor leaves and returns.
    this.#cancelClosing();

    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  // Abort an in-progress close so a fresh hover can reopen the menu immediately.
  #cancelClosing = () => {
    if (!this.#isClosing) return;
    clearTimeout(this.#closeTimeout);
    this.#isClosing = false;
    document.body.classList.remove('mega-menu-closing');
    this.querySelectorAll('[data-animating]').forEach((el) => el.removeAttribute('data-animating'));
  };

  #activateHandler = (event) => {
    this.#cancelClosing();

    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#isInsideBridge = true;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    this.#isClosing = true;
    document.body.classList.add('mega-menu-closing');

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.#isInsideBridge = false;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    this.#closeTimeout = setTimeout(() => {
      item.removeAttribute('data-animating');
      this.#isClosing = false;
      document.body.classList.remove('mega-menu-closing');
    }, CLOSE_ANIMATION_DURATION);
  };

  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
